import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useBroadcastState } from "@/components/BroadcastGraphics";
import { api } from "@/lib/api";
import { MusicBed } from "@/components/MusicBed";
import { WeatherVoice } from "@/components/WeatherVoice";
import { TvFrame, TvTicker } from "@/components/broadcast/TvFrame";
import { useChatTip, ChatTipBug } from "@/components/broadcast/ChatTipBug";
import SlideStinger, { stingerForSlide } from "@/components/broadcast/SlideStinger";
import { AlertTakeover } from "@/components/broadcast/slides/AlertTakeover";
import { OnTheEightsSlide } from "@/components/broadcast/slides/OnTheEightsSlide";
import DashboardStage from "@/components/broadcast/DashboardStage";
import SplashScreen from "@/components/SplashScreen";
import { FailoverSlate, BroadcastErrorBoundary } from "@/components/broadcast/FailoverSlate";
import { Pause, Play, ChevronLeft, ChevronRight, Maximize2, Minimize2, Copy } from "lucide-react";

const BG_COLORS = {
  black: "#000000",
  green: "#00b140",     // Standard chroma-key green
  magenta: "#ff00ff",   // Alt chroma-key
  blue: "#0000ff",      // Blue screen
  transparent: "transparent",
};

export default function BroadcastFullscreen() {
  const [params] = useSearchParams();
  const hideChrome = params.get("chrome") === "off";
  const classic = params.get("layout") === "classic";
  const bg = BG_COLORS[params.get("bg")] ?? "#000000";
  const durationSec = Math.max(3, Math.min(60, Number(params.get("duration")) || 8));
  const durationMs = durationSec * 1000;

  const { slides, idx, setIdx, playing, setPlaying, now, tickerFeed, cutIn, cutInNode, spotlight, spotlightNode, takeover, takeoverNode, eights, eightsNode, cities, cameras, sponsors } = useBroadcastState({ durationMs, capture: hideChrome });
  const chatTip = useChatTip();

  // Preview helpers: ?takeover=1 / ?eights=1 force the Phase-2 graphics on screen
  // (no live warning needed) for QA/screenshots. Highest render priority.
  const forceTakeover = params.get("takeover") === "1";
  const forceEights = params.get("eights") === "1";

  // ?slide=<id> pins one graphic (paused) — handy for previewing a single slide
  const pinned = params.get("slide");
  useEffect(() => {
    if (!pinned || !slides.length) return;
    const i = slides.findIndex((s) => s.id === pinned);
    if (i >= 0) { setIdx(i); setPlaying(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinned, slides.length]);

  const [uiVisible, setUiVisible] = useState(true);
  const [stageScale, setStageScale] = useState(() => Math.min(window.innerWidth / 1920, window.innerHeight / 1080));
  useEffect(() => {
    const onResize = () => setStageScale(Math.min(window.innerWidth / 1920, window.innerHeight / 1080));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [fs, setFs] = useState(false);
  const [copied, setCopied] = useState(false);
  const [voiceSpeaking, setVoiceSpeaking] = useState(false);
  const [promoSpeaking, setPromoSpeaking] = useState(false);
  const [centerVoiceId, setCenterVoiceId] = useState(null);
  const hideTimer = useRef(null);
  const slideIdRef = useRef(null);
  slideIdRef.current = slides[idx]?.id;
  const bootRef = useRef(Date.now());

  // Promo slides duck the music bed under their own voiceover via a window event.
  useEffect(() => {
    const onPromo = (e) => setPromoSpeaking(!!e.detail?.speaking);
    window.addEventListener("gawx-promo-speaking", onPromo);
    return () => window.removeEventListener("gawx-promo-speaking", onPromo);
  }, []);

  // Loop-stall watchdog: if the slide hasn't advanced for a long time while playing
  // (wedged timer / stuck source), reload the encoder page to self-heal.
  const lastAdvanceRef = useRef(Date.now());
  useEffect(() => { lastAdvanceRef.current = Date.now(); }, [idx]);
  useEffect(() => {
    if (!hideChrome || slides.length < 2) return undefined;
    const stallMs = Math.max(90000, durationMs * 8);
    const iv = setInterval(() => {
      if (playing && Date.now() - lastAdvanceRef.current > stallMs) window.location.reload();
    }, 15000);
    return () => clearInterval(iv);
  }, [hideChrome, playing, durationMs, slides.length]);

  // Encoder heartbeat: when captured by a cloud relay (chrome=off), tell the admin panel we're rendering
  useEffect(() => {
    if (!hideChrome) return undefined;
    document.documentElement.classList.add("encoder-lite");
    const beat = () =>
      api.post("/broadcast/heartbeat", {
        source: "browser",
        slide: slideIdRef.current,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        ua: navigator.userAgent,
      }).catch(() => {});
    beat();
    const t = setInterval(beat, 30000);
    const reload = setTimeout(() => window.location.reload(), 6 * 60 * 60 * 1000);
    return () => { clearInterval(t); clearTimeout(reload); document.documentElement.classList.remove("encoder-lite"); };
  }, [hideChrome]);

  // Hide UI after mouse inactivity
  useEffect(() => {
    const scheduleHide = () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(() => setUiVisible(false), 2500);
    };
    const onMove = () => { setUiVisible(true); scheduleHide(); };
    scheduleHide();
    window.addEventListener("mousemove", onMove);
    window.addEventListener("keydown", onMove);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("keydown", onMove);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === " ") { e.preventDefault(); setPlaying((p) => !p); }
      else if (e.key === "ArrowRight") { setIdx((i) => (i + 1) % Math.max(slides.length, 1)); }
      else if (e.key === "ArrowLeft")  { setIdx((i) => (i - 1 + Math.max(slides.length, 1)) % Math.max(slides.length, 1)); }
      else if (e.key.toLowerCase() === "f") { toggleFullscreen(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides.length]);

  // Detect real fullscreen change
  useEffect(() => {
    const onFs = () => setFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  };

  const obsUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/broadcast?chrome=off`;
  }, []);

  const copyObsUrl = async () => {
    try {
      await navigator.clipboard.writeText(obsUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  if (params.get("slate") === "1") {
    return <FailoverSlate />;
  }

  if (!slides.length) {
    // Warming up briefly on boot; if nothing renders after the grace window, show the
    // branded failover slate instead of a blank/dead frame.
    return Date.now() - bootRef.current > 20000
      ? <FailoverSlate note="Reconnecting to the Georgia Weather Network feed…" />
      : <SplashScreen label="Warming Up Broadcast" />;
  }

  const current = slides[idx];
  const timeStr = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  const dayStr = now.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

  // In dashboard mode (default), most loop slides render inside the persistent
  // multi-zone dashboard. A few cinematic full-bleed slides still take the whole
  // frame like commercial breaks: the cold-open intro, on-air bumper, forecast
  // videos and AI promos.
  const FULLSCREEN_LOOP_IDS = new Set(["intro", "bumper", "forecast-video"]);
  const curId = current?.id || "";
  const currentIsFullscreen = FULLSCREEN_LOOP_IDS.has(curId) || curId.startsWith("promo-");
  const useDashboard = !classic && !currentIsFullscreen;

  const atlanta = (cities || []).find((c) => c.id === "atlanta") || (cities || [])[0];
  const tickerUrgent = tickerFeed.some((i) => i.urgent);
  const tickerLabel = tickerUrgent ? "Alert" : "GAWX Live";

  return (
    <div
      data-testid="broadcast-fullscreen"
      className="fixed inset-0 flex items-center justify-center overflow-hidden"
      style={{ background: bg, cursor: uiVisible ? "default" : "none" }}
    >
      {/* Fixed 1920x1080 stage scaled uniformly to fit the viewport so overlays always line up with the map */}
      <div
        className="relative shrink-0"
        data-testid="broadcast-stage"
        style={{ width: 1920, height: 1080, transform: `scale(${stageScale})`, transformOrigin: "center center" }}
      >
        {hideChrome && <MusicBed muted={!!cutIn || !!takeover || curId === "forecast-video"} ducked={voiceSpeaking || promoSpeaking} />}
        {hideChrome && <WeatherVoice cutIn={cutIn} block={!!takeover || !!curId.startsWith("promo-")} slideId={useDashboard ? centerVoiceId : curId} nextSlideId={slides[(idx + 1) % slides.length]?.id} onSpeaking={setVoiceSpeaking} />}
        {/* Slide crossfade (breaking takeover > admin cut-in > on-the-8s > loop) */}
        {forceTakeover ? (
          <div key="takeover-preview" data-testid="fs-slide-takeover" className="absolute inset-0 broadcast-fade">
            <AlertTakeover alert={{ title: "Tornado Warning", area: "Fulton, Cobb, DeKalb, Gwinnett, Clayton" }} playTone={false} />
          </div>
        ) : forceEights ? (
          <div key="eights-preview" data-testid="fs-slide-eights" className="absolute inset-0 broadcast-fade">
            <OnTheEightsSlide now={now} cities={cities} />
          </div>
        ) : takeover ? (
          <div key={`takeover-${takeover.id}`} data-testid="fs-slide-takeover" className="absolute inset-0 broadcast-fade">
            <BroadcastErrorBoundary autoReload={hideChrome}>{takeoverNode}</BroadcastErrorBoundary>
          </div>
        ) : cutIn ? (
          <div key={`cutin-${cutIn.id}`} data-testid="fs-slide-cutin" className="absolute inset-0 broadcast-fade">
            <BroadcastErrorBoundary autoReload={hideChrome}>{cutInNode}</BroadcastErrorBoundary>
          </div>
        ) : spotlight ? (
          <div key={`spotlight-${spotlight.id}`} data-testid="fs-slide-spotlight" className="absolute inset-0 broadcast-fade">
            <BroadcastErrorBoundary autoReload={hideChrome}>{spotlightNode}</BroadcastErrorBoundary>
          </div>
        ) : eights ? (
          <div key={`eights-${eights.slot}`} data-testid="fs-slide-eights" className="absolute inset-0 broadcast-fade">
            <BroadcastErrorBoundary autoReload={hideChrome}>{eightsNode}</BroadcastErrorBoundary>
          </div>
        ) : (
          useDashboard ? (
            <div key="gwn-dashboard" data-testid="fs-dashboard" className="absolute inset-0">
              <BroadcastErrorBoundary autoReload={hideChrome}>
                <DashboardStage
                  cities={cities}
                  cameras={cameras}
                  sponsors={sponsors}
                  loopSlides={slides}
                  onCenterChange={setCenterVoiceId}
                  pinnedCenter={pinned}
                />
              </BroadcastErrorBoundary>
            </div>
          ) : (
            <div key={current.id} data-testid={`fs-slide-${current.id}`} className="absolute inset-0 broadcast-fade">
              <BroadcastErrorBoundary autoReload={hideChrome}>
                <TvFrame title={current.title} right={current.right} bugTemp={atlanta?.current_temp} fullBleed={current.fullBleed} chromeless={current.chromeless} coldOpen={current.coldOpen} asOf={current.asOf} noDog={(current.id || "").startsWith("promo-")} hasTicker>
                  {current.node}
                </TvFrame>
              </BroadcastErrorBoundary>
            </div>
          )
        )}

        {/* Smart-routed stinger: matches the board we're transitioning into */}
        <SlideStinger key={`sting-${takeover ? `tk-${takeover.id}` : cutIn ? `cutin-${cutIn.id}` : eights ? `e8-${eights.slot}` : current.id}`} variant={takeover || cutIn ? "radar" : eights ? "wipe" : stingerForSlide(current)} testId="fs-stinger" />

        {/* Bottom ticker: admin news items, otherwise live statewide conditions */}
        {!cutIn && !takeover && <TvTicker label={tickerLabel} items={tickerFeed} urgent={tickerUrgent} />}

        {/* Periodic on-screen '!wx <town>' chat tip */}
        {!cutIn && !takeover && !spotlight && <ChatTipBug tip={chatTip} />}

        {/* Scanline overlay (subtle) */}
        {!hideChrome && (
          <div className="pointer-events-none absolute inset-0 z-10 broadcast-scan opacity-[0.05]" />
        )}

        {/* Broadcast chrome overlays (top/bottom) */}
        {!hideChrome && (
          <>
            {/* Progress bar */}
            <div className="absolute inset-x-0 bottom-0 z-10 h-1 bg-white/10">
              <div
                key={`${idx}-${playing ? "p" : "s"}`}
                className="h-full bg-[#d90429]"
                style={{
                  width: playing ? "100%" : "0%",
                  transition: playing ? `width ${durationMs}ms linear` : "none",
                }}
              />
            </div>
          </>
        )}

        {/* Studio toolbar (auto-hides). Fully excluded under the "chrome=off" capture URL
            so a stray mouse/keyboard event in OBS can never flash it on air. */}
        {!hideChrome && (
        <div
          data-testid="fs-toolbar"
          className={`pointer-events-auto absolute right-4 top-1/2 z-30 -translate-y-1/2 transition-opacity duration-300 ${uiVisible ? "opacity-100" : "opacity-0"}`}
        >
          <div className="flex flex-col items-center gap-2 rounded-full border border-white/15 bg-black/80 px-2 py-3 backdrop-blur">
            <button
              data-testid="fs-prev"
              onClick={() => setIdx((idx - 1 + slides.length) % slides.length)}
              className="flex h-9 w-9 items-center justify-center rounded-full text-white hover:bg-white/10"
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              data-testid="fs-toggle"
              onClick={() => setPlaying(!playing)}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-[#d90429] text-white hover:bg-[#ef233c]"
              aria-label={playing ? "Pause" : "Play"}
            >
              {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </button>
            <button
              data-testid="fs-next"
              onClick={() => setIdx((idx + 1) % slides.length)}
              className="flex h-9 w-9 items-center justify-center rounded-full text-white hover:bg-white/10"
              aria-label="Next slide"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <div className="my-1 h-px w-6 bg-white/15" />
            <button
              data-testid="fs-fullscreen"
              onClick={toggleFullscreen}
              className="flex h-9 w-9 items-center justify-center rounded-full text-white hover:bg-white/10"
              aria-label="Toggle browser fullscreen"
            >
              {fs ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
          </div>
        </div>
        )}

        {/* OBS helper card (auto-hides). Hidden entirely on the capture URL. */}
        {!hideChrome && (
        <div
          data-testid="fs-obs-helper"
          className={`pointer-events-auto absolute left-4 top-1/2 z-30 -translate-y-1/2 max-w-xs transition-opacity duration-300 ${uiVisible ? "opacity-100" : "opacity-0"}`}
        >
          <div className="rounded border border-white/10 bg-black/80 p-3 backdrop-blur">
            <div className="font-oswald text-[10px] font-bold uppercase tracking-[0.3em] text-[#d90429]">
              OBS Browser Source
            </div>
            <div className="mt-1 break-all font-mono text-[10px] text-gray-300">{obsUrl}</div>
            <button
              onClick={copyObsUrl}
              data-testid="fs-copy-url"
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-sm border border-white/15 px-2 py-1.5 font-oswald text-[10px] font-bold uppercase tracking-widest text-white hover:border-[#d90429] hover:text-[#d90429]"
            >
              <Copy className="h-3 w-3" /> {copied ? "Copied!" : "Copy URL"}
            </button>
            <div className="mt-2 text-[10px] leading-relaxed text-gray-400">
              1920×1080 · press <kbd className="rounded bg-white/10 px-1">F</kbd> to fullscreen ·{" "}
              <kbd className="rounded bg-white/10 px-1">Space</kbd> pause ·{" "}
              <kbd className="rounded bg-white/10 px-1">←/→</kbd> nav
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
